import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity, ScrollView } from "react-native";
import ToothFairyButton from "../ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../ToothFairyComponents/ToothFairyModal";

const VersionDiffSummary = ({
  isVisible,
  onClose,
  onViewFullDiff,
  onRollback,
  snapshotVersion,
  changes,
  datetime,
  changedBy,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const [expandedCategories, setExpandedCategories] = useState(
    Object.keys(changes?.groupedChanges || {}).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}),
  );

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const categoryKeyMap = {
    "Model Settings": "versionDiff.categories.modelSettings",
    "Tools & Capabilities": "versionDiff.categories.toolsCapabilities",
    "Knowledge": "versionDiff.categories.knowledge",
    "Behavior": "versionDiff.categories.behavior",
    "Memory & History": "versionDiff.categories.memoryHistory",
    "Moderation": "versionDiff.categories.moderation",
    "Voice & Language": "versionDiff.categories.voiceLanguage",
    "Permissions": "versionDiff.categories.permissions",
    "Identity": "versionDiff.categories.identity",
    "User Experience": "versionDiff.categories.userExperience",
    "Code Execution": "versionDiff.categories.codeExecution",
    "Desktop": "versionDiff.categories.desktop",
    "Summarisation": "versionDiff.categories.summarisation",
  };

  const categorizedChanges = useMemo(() => {
    if (!changes?.groupedChanges) return [];

    return Object.entries(changes.groupedChanges)
      .filter(([category, items]) => items.length > 0)
      .sort(([a], [b]) => {
        const priorityOrder = [
          "Model Settings",
          "Tools & Capabilities",
          "Knowledge",
          "Behavior",
          "Memory & History",
          "Moderation",
          "Voice & Language",
          "Permissions",
          "Identity",
          "User Experience",
          "Code Execution",
          "Desktop",
          "Summarisation",
        ];
        const aIndex = priorityOrder.indexOf(a);
        const bIndex = priorityOrder.indexOf(b);

        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.localeCompare(b);
      });
  }, [changes]);

  const renderChangeItem = (change, index) => {
    const getChangeIcon = () => {
      switch (change.changeType) {
        case "added":
          return AppAssets.icons.plus;
        case "removed":
          return AppAssets.icons.minus;
        case "modified":
          return AppAssets.icons.edit;
        default:
          return AppAssets.icons.edit;
      }
    };

    const getChangeColor = () => {
      switch (change.changeType) {
        case "added":
          return currentTheme?.theme?.green || "#28a745";
        case "removed":
          return currentTheme?.theme?.red || "#dc3545";
        case "modified":
          return currentTheme?.theme?.orange || "#ffc107";
        default:
          return currentTheme?.theme?.mediumGrey || "#6c757d";
      }
    };

    const renderValueChange = () => {
      if (change.changeType === "added") {
        return (
          <AppText
            preventTranslation
            style={{ fontSize: 13, color: getChangeColor() }}
          >
            {`+ ${change.newDisplay}`}
          </AppText>
        );
      } else if (change.changeType === "removed") {
        return (
          <AppText
            preventTranslation
            style={{ fontSize: 13, color: getChangeColor() }}
          >
            {`- ${change.oldDisplay}`}
          </AppText>
        );
      } else if (change.changeType === "modified") {
        return (
          <View style={{ flexDirection: "column", gap: 2 }}>
            <AppText
              preventTranslation
              style={{ fontSize: 13, color: currentTheme?.theme?.red || "#dc3545" }}
            >
              {`- ${change.oldDisplay}`}
            </AppText>
            <AppText
              preventTranslation
              style={{ fontSize: 13, color: currentTheme?.theme?.green || "#28a745" }}
            >
              {`+ ${change.newDisplay}`}
            </AppText>
          </View>
        );
      }
      return null;
    };

    return (
      <View
        key={`${change.field}-${index}`}
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          paddingVertical: 6,
          paddingHorizontal: 10,
          marginLeft: 20,
          borderLeftWidth: 2,
          borderLeftColor: currentTheme?.theme?.border_color || "#e0e0e0",
        }}
      >
        <View style={{ marginRight: 8, marginTop: 2 }}>
          <AppIcon icon={getChangeIcon()} size={14} color={getChangeColor()} />
        </View>
        <View style={{ flex: 1 }}>
          <AppText
            preventTranslation
            style={{
              fontSize: 13,
              fontWeight: "600",
              color: currentTheme?.theme?.fontColor,
              marginBottom: 2,
            }}
          >
            {change.label}
          </AppText>
          {renderValueChange()}
        </View>
      </View>
    );
  };

  const renderCategory = ([category, items]) => {
    const isExpanded = expandedCategories[category];
    const changeCount = items.length;

    return (
      <View key={category} style={{ marginBottom: 12 }}>
        <TouchableOpacity
          onPress={() => toggleCategory(category)}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor:
              currentTheme?.theme?.softBackgroundColor || "#f5f5f5",
            paddingVertical: 10,
            paddingHorizontal: 12,
            borderRadius: 8,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
            <AppIcon
              icon={
                isExpanded
                  ? AppAssets.icons.arrow_down
                  : AppAssets.icons.arrow_right
              }
              size={16}
              color={currentTheme?.theme?.primary}
            />
            <AppText
              style={{
                fontSize: 14,
                fontWeight: "bold",
                color: currentTheme?.theme?.fontColor,
                marginLeft: 8,
              }}
            >
              {t(categoryKeyMap[category] || category)}
            </AppText>
          </View>
          <View
            style={{
              backgroundColor: currentTheme?.theme?.primary,
              paddingHorizontal: 10,
              paddingVertical: 4,
              borderRadius: 12,
              minWidth: 30,
              alignItems: "center",
            }}
          >
            <AppText
              preventTranslation
              style={{
                color: currentTheme?.theme?.white || "white",
                fontSize: 12,
                fontWeight: "bold",
              }}
            >
              {changeCount}
            </AppText>
          </View>
        </TouchableOpacity>

        {isExpanded && (
          <View style={{ marginTop: 8 }}>
            {items.map((change, index) => renderChangeItem(change, index))}
          </View>
        )}
      </View>
    );
  };

  return (
    <ToothFairyModal
      isModalOpen={isVisible}
      setIsModalOpen={onClose}
      modalStyle={{
        maxWidth: 700,
        width: "90%",
        height: "85%",
      }}
      modalTitle={t("versionDiff.changesSummaryTitle", { version: snapshotVersion })}
      closeModal={onClose}
    >
      <View style={{ flex: 1, padding: 20 }}>
        <View style={{ marginBottom: 20 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <AppIcon
              icon={AppAssets.icons.day}
              size={16}
              color={currentTheme?.theme?.primary}
            />
            <AppText
              style={{ fontSize: 14, color: currentTheme?.theme?.fontColor }}
            >
              {t("versionDiff.changedOn", { datetime })}
            </AppText>
          </View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <AppIcon
              icon={AppAssets.icons.userInfo}
              size={16}
              color={currentTheme?.theme?.primary}
            />
            <AppText
              style={{ fontSize: 14, color: currentTheme?.theme?.fontColor }}
            >
              {t("versionDiff.changedBy", { user: changedBy })}
            </AppText>
          </View>
        </View>

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
        >
          {categorizedChanges.map((entry) => renderCategory(entry))}
        </ScrollView>

        <View
          style={{
            flexDirection: "row",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 20,
            paddingTop: 20,
            borderTopWidth: 1,
            borderTopColor: currentTheme?.theme?.border_color || "#e0e0e0",
          }}
        >
          <ToothFairyButton
            title={t("versionDiff.viewFullDiff")}
            onPress={onViewFullDiff}
            fontColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.orange}
            isSecondary
            wrapperStyle={{ minWidth: 120 }}
          />
          <ToothFairyButton
            title={t("versionDiff.rollback")}
            onPress={onRollback}
            wrapperStyle={{ minWidth: 100 }}
          />
        </View>
      </View>
    </ToothFairyModal>
  );
};

export default VersionDiffSummary;
